/* eslint-disable import/no-cycle */ // TODO: {@link https://scalyr.myjetbrains.com/youtrack/issue/FRONT-4218}
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import FullscreenIcon from '@mui/icons-material/AspectRatioOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import CodeIcon from '@mui/icons-material/Code';
import CopyIcon from '@mui/icons-material/FileCopyOutlined';
import SearchIcon from '@mui/icons-material/Search';
import RedoIcon from '@mui/icons-material/Redo';

import { MenuItemConfig, UrlParams } from 'common/types';
import { buildUrl } from 'common/util/location';
import { useSetSearchParams } from 'hooks/useSetSearchParams';

import IconButton, { IconButtonProps } from 'components/common/IconButton/IconButton';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import DropdownItems, { IconTextPair } from 'components/common/DropdownItems/DropdownItems';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import { useMutation } from '@apollo/client';
import {
  ADD_GRAPH_TO_DASHBOARD,
  DELETE_DASHBOARD_GRAPH,
  GET_CONFIGV2,
  MOVE_GRAPHS_BETWEEN_TABS,
} from 'components/dashboards/queries';
import * as K from 'common/constants';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { handleExport } from 'components/common/Graphs/ScalyrGraph';
import {
  queryTimeRangeState,
  searchParamRecordState,
  searchParamState,
} from 'components/common/Managers/RouteStateManager.state';
import DashboardChartLegend from 'components/dashboards/DashboardPage/DashboardChartLegend';
import { DashboardParam, GraphConfigType } from 'components/dashboards/types';
import AddGraphModal, { GraphType } from 'components/dashboards/DashboardPage/modals/AddGraphModal';
import EditPanel from 'components/dashboards/DashboardPage/modals/EditPanel';
import EditGraphJSON from 'components/dashboards/DashboardPage/modals/EditGraphJSON';
import { recalcBarWidths } from 'components/dashboards/DashboardPage/DashboardHighcharts';
import { NavPlotToSearch } from 'components/dashboards/DashboardPage/modals/PlotZoom';
import ELEMENT from 'common/element';
import { isFalsy } from 'common/util/language';
import { dashboardTabsState, prefilterState } from 'components/dashboards/dashboards.state';
import { plotsStylesState } from 'styles/stylesProvider';

import './DashboardGraph.scss';

import {
  createDistributionURL,
  createEventsPageUrl,
  DASH_VIZ_TYPE,
  DashboardPlotData,
  getExploreHref,
  getPlotSettings,
  getType,
  substituteParameters,
} from '../dashboardData';

export interface DashboardGraphDisplayType {
  dashboardName: string;
  title?: string;
  label?: string;
  index: number;
  config: GraphConfigType;
  dataIds: string[];
  params: Array<DashboardParam>;
  isDraggable: boolean;
  child: JSX.Element;
  highChartInstance?: Highcharts.Chart;
  hasWriteAccess: boolean;
}

const SearchIconButton = (additionalSettings: Partial<IconButtonProps>): JSX.Element => (
  <IconButton
    tooltip="View in Search"
    className="DashboardGraph__SearchIcon"
    icon={<SearchIcon fontSize="small" />}
    testId={ELEMENT.dashboardViewInSearchIcon}
    {...additionalSettings}
  />
);

const isSearchable = (vizType: DASH_VIZ_TYPE): boolean =>
  [vizType.isPowerQuery, vizType.isReport, vizType.isMarkdown, vizType.isDistribution].every(isFalsy);

type PanelBasedSearchIconButtonProps = {
  vizType: DASH_VIZ_TYPE;
  exploreHref: string | undefined;
  startTime: string;
  endTime: string;
  config: GraphConfigType;
  setNavToSearch: Dispatch<SetStateAction<boolean>>;
  plots?: Array<DashboardPlotData>;
  prefilter?: string;
};

const PanelBasedSearchIconButton = ({
  vizType,
  exploreHref,
  startTime,
  endTime,
  config,
  setNavToSearch,
  plots,
  prefilter = '',
}: PanelBasedSearchIconButtonProps): JSX.Element | null => {
  if (vizType.isPowerQuery && exploreHref) {
    return <SearchIconButton href={exploreHref} />;
  }

  if (vizType.isDistribution && config.facet) {
    const teamEmails = config.teamEmails ? config.teamEmails.join(',') : undefined;
    return <SearchIconButton href={createDistributionURL(config.facet, teamEmails)} />;
  }

  if (isSearchable(vizType) && plots?.length === 1) {
    return (
      <SearchIconButton href={createEventsPageUrl(startTime, endTime, config, plots, 0, true, undefined, prefilter)} />
    );
  }

  if (isSearchable(vizType) && plots?.length !== 1) {
    return <SearchIconButton onClick={(): void => setNavToSearch(true)} />;
  }

  return null;
};

/**
 * This component displays a graph (i.e., chart or table) within a dashboard grid, as well as
 * handles item editing, pivoting to search, graphs, etc.
 *
 * Some ot the props below are not yet hooked up.
 *
 * @prop title             The configured name for this graph
 * @prop index             Index of this graph within the parent -- will be used to show any edit UI
 * @prop config            The config stanza for just this graph -- will be used to show any edit UI
 * @prop dataIds           The list of LRQ data ids to retreive data
 * @prop params            The current list of params and their values (for updating filters, etc.)
 * @prop child             The actual data displayer, either a chart or a table
 * @prop isChart           true if it's a time series graph (line/bar/area), false otherwise
 * @prop isDarkMode        true if we're using darkk mode
 * @prop dashboardName     The dashboard name used to refetch a query
 * @prop highChartInstance The Highcharts chart instance (if Highcharts)
 */
const DashboardGraph = ({
  title,
  index,
  config,
  dataIds,
  params,
  child,
  isDraggable,
  dashboardName,
  highChartInstance,
  hasWriteAccess,
}: DashboardGraphDisplayType): JSX.Element => {
  const [isTooltipActive, setIsTooltipActive] = useState(false);
  const [showMoreVertOverlay, setShowMoreVertOverlay] = useState(false);
  const [showEditGraph, setShowEditGraph] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [showEditJSON, setShowEditJSON] = useState(false);
  const [navToSearch, setNavToSearch] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const plotsStyles = useRecoilValue(plotsStylesState);
  const searchParams = useRecoilValue(searchParamRecordState);
  const preFilter = useRecoilValue(prefilterState);
  const tabName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
  const tabsList = useRecoilValue(dashboardTabsState);

  const vizType = getType(config);

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const setSearchParams = useSetSearchParams();
  const [deleteDashboardGraph] = useMutation(DELETE_DASHBOARD_GRAPH, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName, startTime, endTime } }],
  });
  const [cloneDashboardGraph] = useMutation(ADD_GRAPH_TO_DASHBOARD, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName, startTime, endTime } }],
  });
  const [movePanelToAnotherTab] = useMutation(MOVE_GRAPHS_BETWEEN_TABS, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName, startTime, endTime } }],
  });

  const graphTitle = substituteParameters(params, title);

  const onEnterTooltip = useCallback(() => {
    setIsTooltipActive(true);
  }, []);

  const onExitedTooltip = useCallback(() => {
    setIsTooltipActive(false);
  }, []);

  const handleDeleteGraph = (): void => {
    deleteDashboardGraph({
      variables: {
        dashboardName,
        graphIndex: index,
        tabName,
      },
    })
      .then(() => {
        setPageAlertConfig({ message: `${graphTitle} deleted`, variant: PageAlertVariant.SUCCESS, autoHide: true });
      })
      .catch((error) => {
        setPageAlertConfig({ message: error.message, variant: PageAlertVariant.ERROR, autoHide: true });
      });
  };

  const zoomHref = buildUrl(K.ROUTE.DASHBOARD, {
    ...searchParams,
    [K.URL_PARAM.DASHBOARD_NAME]: dashboardName,
    [K.URL_PARAM.DASHBOARD_VIEW_GRAPH]: `${index}`,
  } as UrlParams);
  const exploreHref = getExploreHref(config, params, startTime, endTime, preFilter);

  // Plots for plot zoom dialog
  const plots = config?.plots?.map((plotConfig, plotIndex) => {
    const settings = getPlotSettings(config, plotConfig, params);
    return { ...settings, color: plotsStyles[plotIndex] };
  });

  // All the menu actions
  const editJSON = {
    children: <IconTextPair leftIcon={<CodeIcon fontSize="small" />} label="Edit JSON" />,
    clickHandler: (): void => {
      setShowMoreVertOverlay(false);
      setShowEditJSON(true);
    },
  };

  const movePanelToTab = (destinationTabName: string) => {
    movePanelToAnotherTab({
      variables: {
        dashboardName,
        destinationTabName,
        graphIndex: index,
        sourceTabName: tabName,
      },
    })
      .then(() => {
        setPageAlertConfig({
          message: `${graphTitle} Panel is moved to ${destinationTabName} tab`,
          variant: PageAlertVariant.SUCCESS,
          autoHide: true,
        });
      })
      .catch((error) => {
        setPageAlertConfig({ message: error.message, variant: PageAlertVariant.ERROR, autoHide: true });
      })
      .finally(() => {
        setShowMoreVertOverlay(false); // close the dropdown menu
      });
  };

  const movePanel = {
    children: <IconTextPair leftIcon={<RedoIcon fontSize="small" />} label="Move to Tab" />,
    subMenuItems: tabsList
      .filter((tab: string) => tabName !== tab)
      .map((tab: string) => ({
        text: tab,
        clickHandler: () => movePanelToTab(tab),
      })),
  };

  const cloneGraph = {
    children: <IconTextPair leftIcon={<CopyIcon fontSize="small" />} label="Clone" />,
    clickHandler: (): void => {
      const newConfig = JSON.stringify({ ...config }, null, 2);
      cloneDashboardGraph({
        variables: {
          dashboardName,
          graph: newConfig,
          tabName,
        },
      })
        .then(() => {
          setPageAlertConfig({ message: `${graphTitle} cloned`, variant: PageAlertVariant.SUCCESS, autoHide: true });
        })
        .catch((error) => {
          setPageAlertConfig({ message: error.message, variant: PageAlertVariant.ERROR, autoHide: true });
        })
        .finally(() => {
          setShowMoreVertOverlay(false); // close the dropdown menu
        });
    },
  };

  const editGraph = {
    children: <IconTextPair leftIcon={<EditIcon fontSize="small" />} label="Edit Graph" />,
    clickHandler: (): void => {
      setShowMoreVertOverlay(false);
      setShowEditGraph(true);
    },
  };

  const editPanel = {
    children: <IconTextPair leftIcon={<EditIcon fontSize="small" />} label="Edit Panel" />,
    clickHandler: (): void => {
      setShowMoreVertOverlay(false);
      setShowEditPanel(true);
    },
  };

  const downloadPNG = {
    children: <IconTextPair leftIcon={<ArrowDownwardIcon fontSize="small" />} label="Download PNG" />,
    clickHandler: (): void => {
      setShowMoreVertOverlay(false);
      if (highChartInstance) {
        handleExport(highChartInstance, graphTitle);
      }
    },
  };

  const deleteGraph = {
    children: <IconTextPair leftIcon={<DeleteIcon fontSize="small" />} label="Delete" />,
    clickHandler: (): void => {
      setShowMoreVertOverlay(false);
      setShowDeleteModal(true);
    },
  };

  // Construct the sections for the "more" menu dropdown based on the vizualization type.
  const getMenuSections = (): Array<Array<MenuItemConfig>> => {
    const menuSections = [];
    if (vizType.isChart) {
      if (vizType.isPowerQuery) {
        menuSections.push(vizType.isPie ? [editPanel, downloadPNG] : [downloadPNG]);
      } else {
        menuSections.push([editGraph, downloadPNG]);
      }
    } else if (!vizType.isReport) {
      menuSections.push([editPanel]);
    }
    if (tabsList.length > 0) {
      menuSections.push([editJSON, cloneGraph, movePanel], [deleteGraph]);
    } else {
      menuSections.push([editJSON, cloneGraph], [deleteGraph]);
    }
    return menuSections;
  };

  // The menu itself
  let moreMenu = [[downloadPNG]] as Array<Array<MenuItemConfig>>;
  // Only construct full menu for users with WRITE access.
  if (hasWriteAccess) {
    moreMenu = getMenuSections();
  }

  const moreVertOverlay = (
    <div className="DashboardGraph__MoreVertOverlayMenu">
      <DropdownItems sections={moreMenu} />
    </div>
  );

  const MoreMenu = (
    <OverlayTrigger
      overlay={moreVertOverlay}
      placement="bottom-end"
      className="DashboardGraph__MoreVertOverlay"
      showOverlay={showMoreVertOverlay}
      setShowOverlay={setShowMoreVertOverlay}
    >
      <IconButton
        tooltip="More"
        showTooltip={!showMoreVertOverlay}
        onExitedTooltip={onExitedTooltip}
        onEnterTooltip={onEnterTooltip}
        icon={<MoreVertIcon />}
      />
    </OverlayTrigger>
  );

  // if the menu is shown, make the title bar visible
  const TitleBarClass = `DashboardGraph__TitleBar ${
    (showMoreVertOverlay || isTooltipActive) && 'DashboardGraph__TitleBar--Visible'
  } ${isDraggable && 'DashboardGraph__TitleBar--Draggable'}`;

  const openViewGraph = (): void => {
    setSearchParams({ [K.URL_PARAM.DASHBOARD_VIEW_GRAPH]: index.toString() } as UrlParams);
  };

  const legend = vizType.isTimeseries ? (
    <DashboardChartLegend
      dataIds={dataIds}
      dashboardName={dashboardName}
      config={config}
      params={params}
      plotColors={plotsStyles}
      highChartInstance={highChartInstance}
      chartIndex={index}
    />
  ) : null;

  return (
    <>
      <div
        className={`DashboardGraph ${vizType.isHoneyComb && 'DashboardGraphHoneyComb'}`}
        data-test-id={ELEMENT.dashboardGraph}
      >
        <div className={TitleBarClass}>
          <div className="DashboardGraph__TitleBarTitle" data-test-id={ELEMENT.dashboardGraphTitle}>
            {graphTitle}
          </div>
          <div className="DashboardGraph__TitleBarIcons" data-test-id={ELEMENT.dashboardGraphTitleIcons}>
            <PanelBasedSearchIconButton
              vizType={vizType}
              exploreHref={exploreHref}
              startTime={startTime}
              endTime={endTime}
              config={config}
              setNavToSearch={setNavToSearch}
              plots={plots}
              prefilter={preFilter}
            />
            {(vizType.isTimeseries || vizType.isPie) && !vizType.isReport && (
              <IconButton
                tooltip="View Larger"
                className="DashboardGraph__FullScreenIcon"
                href={zoomHref}
                onClick={config.plots && config.graphStyle !== 'number' ? openViewGraph : undefined}
                icon={<FullscreenIcon fontSize="small" />}
              />
            )}
            {MoreMenu}
          </div>
        </div>
        {showEditJSON && (
          <EditGraphJSON
            graphIndex={index}
            graphConfig={config}
            showModal={showEditJSON}
            handleClose={(): void => setShowEditJSON(false)}
          />
        )}
        {showEditGraph && (
          <AddGraphModal
            isAddNew={false}
            dashboardName={dashboardName}
            graphType={config.breakdownFacet ? GraphType.BREAKDOWN : GraphType.STANDARD}
            graphConfig={config}
            showModal={showEditGraph}
            handleClose={(): void => setShowEditGraph(false)}
            graphIndex={index}
          />
        )}
        {showEditPanel && (
          <EditPanel
            dashboardName={dashboardName}
            incomingConfig={config}
            showModal={showEditPanel}
            handleClose={(): void => setShowEditPanel(false)}
            panelIndex={index}
          />
        )}
        {showDeleteModal && (
          <ConfirmDialog
            title="Please Confirm"
            text="Are you sure you want to delete this?"
            show={showDeleteModal}
            onSubmit={handleDeleteGraph}
            close={(): void => setShowDeleteModal(false)}
            cannotBeUndone
          />
        )}
        {child}
      </div>
      <NavPlotToSearch
        graphConfig={config}
        plots={plots}
        doNavigate={navToSearch}
        onClose={(): void => setNavToSearch(false)}
      />
      {legend}
    </>
  );
};

/**
 * This is used by the parent (the grid) to tell a Highcharts chart to reflow itself. If we don't do
 * this, charts won't resize down.
 *
 * @prop chart Highcharts object instance as returned by Hicharts during chart creation.
 */
export const resizeHighcharts = (chart: Highcharts.Chart): void => {
  setTimeout(() => {
    // I know it looks crazy to test all these, but Highcharts' state is inconsistent at best
    if (chart && chart.options && chart.options.chart) {
      recalcBarWidths(chart);
      chart.reflow();
    }
  }, 0);
};

export default DashboardGraph;
